import React, { useState } from "react";

import { Card, Input, Button, Toggle, TextArea , Dropdown} from "selfly-ui-react";

import keyExists from "../../../utils/keyExists";
import useFlags from "../../../utils/useFlags";

import { useGlobalStore } from "../../../services/GlobalStoreService/GlobalStoreHooks";

import "./device-meta-editor.scss";
import { JOKI } from "../../../JOKI";

export default function DeviceMetaEditor(props) {
    const origMeta = parseDeviceMetaData(props.device);
    const [meta, setMeta] = useState(origMeta);

    const [invalid, setInvalid, setValid] = useFlags();
    const [changed, setChanged] = useFlags();

    const [mode, setMode] = useGlobalStore("mode", "READ");

    // console.log("DEVICE TO BE EDITED", props.device, parseDeviceMetaData(props.device));

    function updateDraft(key, value, valid = true) {
        if (valid) {
            const newMeta = Object.assign({}, meta);
            newMeta[key] = value;
            setMeta(newMeta);
            setValid(key);
            setChanged(key);
        } else {
            setInvalid(key);
        }
    }

    function save() {
        JOKI.trigger({
            to: "DevicesService",
            key: "updateMeta",
            body: {
                device: props.device,
                metaData: meta,
            },
        });
        setMode("READ");
    }

    function cancel() {
        setMode("READ");
    }

    // console.log("INVALID:\t", invalid, "\nCHANGED:\t", changed);

    if (mode === "READ") {
        return null;
    }

    return (
        <div className="device-meta-editor">
            <h3>Edit Device Information</h3>

            <Card title="Device information">
                <MetaInput
                    label="Device Name"
                    metaKey="name"
                    meta={meta}
                    onChange={updateDraft}
                    validationType="string"
                    id="DeviceMetaName"
                />

                <MetaInput
                    label="Description"
                    metaKey="description"
                    meta={meta}
                    onChange={updateDraft}
                    validationType="string"
                    id="DeviceMetaDescription"
                />
            </Card>

            <Card title="Address information">
                <MetaInput
                    label="Street Address"
                    metaKey="street"
                    meta={meta}
                    onChange={updateDraft}
                    validationType="string"
                    id="DeviceMetaAddressStreet"
                />

                <MetaInput
                    label="Street 2"
                    metaKey="street2"
                    meta={meta}
                    onChange={updateDraft}
                    validationType="string"
                    id="DeviceMetaAddressStreet2"
                />

                <MetaInput label="City" metaKey="city" meta={meta} onChange={updateDraft} validationType="string" id="DeviceMetaAddressCity" />

                <MetaInput label="State" metaKey="state" meta={meta} onChange={updateDraft} validationType="string" id="DeviceMetaAddressState" />

                <MetaInput label="Zip Code" metaKey="zipcode" meta={meta} onChange={updateDraft} validationType="string" id="DeviceMetaAddressZipcode" />

                <MetaInput
                    label="Country"
                    metaKey="country"
                    meta={meta}
                    onChange={updateDraft}
                    validationType="string"
                    id="DeviceMetaAddressCountry"
                />
            </Card>

            <Card title="Positional Data">
                <MetaInput label="Longitude" metaKey="lon" meta={meta} onChange={updateDraft} validationType="float" id="DeviceMetaCoordinatesLon" />
                <MetaInput label="Latitude" metaKey="lat" meta={meta} onChange={updateDraft} validationType="float" id="DeviceMetaCoordinatesLat" />
            </Card>

            <Card title="Meta data">
                <MetaInput
                    label="Meta data as json"
                    metaKey="metaJson"
                    meta={meta}
                    onChange={updateDraft}
                    validationType="json"
                    id="DeviceMetaJson"
                />
            </Card>

            <div className="device-meta-editor-buttons">
                <Button icon="cancel" type="negative" onClick={cancel}>
                    Cancel
                </Button>
                <Button
                    icon="save"
                    type="positive"
                    disabled={changed.length === 0 || invalid.length > 0}
                    onClick={save}
                    id="ButtonDeviceMetaEditorSave"
                >
                    Save
                </Button>
            </div>
        </div>
    );
}

function MetaInput(props) {
    const [initVal] = useState(props.meta[props.metaKey]);
    const [value, setValue] = useState(initVal);
    const [valid, setValidity] = useState(true);

    function changeEventHandler(e) {
        change(e.currentTarget.value);
    }

    function change(curVal) {
        let sendValue = curVal;
        let isValid = true;
        switch (props.validationType) {
            case "string":
                isValid = typeof curVal === "string";
                break;
            case "float":
                isValid = curVal.length === 0 ? false : isNaN(curVal) ? false : true;
                sendValue = parseFloat(curVal);
                break;
            case "integer":
                isValid =
                    curVal.length === 0
                        ? false
                        : isNaN(curVal)
                        ? false
                        : Number.isInteger(Number(curVal))
                        ? true
                        : false;
                sendValue = parseInt(curVal);
                break;
            case "boolean":
                isValid = curVal === true || curVal === false;
                break;
            case "text":
                isValid = true;
                break;
            case "json":
                try {
                    JSON.parse(curVal);
                    // console.log("VALID JSON", curVal);
                    isValid = true;
                } catch (err) {
                    // console.log("INVALID JSON", curVal);
                    isValid = false;
                }
                break;
            case "select":
                break;
            default:
                break;
        }

        props.onChange(props.metaKey, sendValue, isValid);
        // console.log("set validity", isValid);
        setValidity(isValid);
        setValue(curVal);
    }

    if (initVal === undefined) {
        return null;
    }

    if (props.validationType === "boolean") {
        return (
            <div className="se-input-container">
                <label className="se-label">{props.label}</label>
                <Toggle isActive={value} onChange={change} id={props.id}/>
            </div>
        );
    }

    if (props.validationType === "select") {
        return (
            <div className="se-input-container">
                <Dropdown label={props.label} options={props.options} value={value} id={props.id}/>
            </div>
        );
    }

    if (props.validationType === "text") {
        return <TextArea label={props.label} placeholder="Enter ext" value={value} onChange={changeEventHandler} id={props.id}/>;
    }

    if (props.validationType === "json") {
        return (
            <TextArea
                label={props.label}
                placeholder="Enter Json"
                value={value}
                onChange={changeEventHandler}
                className={!valid ? "invalid" : value === initVal ? "original" : "changed"}
                id={props.id}
            />
        );
    }

    return (
        <Input
            label={props.label}
            value={value}
            onChange={changeEventHandler}
            className={!valid ? "invalid" : value === initVal ? "original" : "changed"}
            id={props.id}
        />
    );
}

function parseDeviceMetaData(device) {
    const meta = {
        lon: 0,
        lat: 0,
        name: device.name !== null ? device.name : "",
        description: device.description !== null ? device.description : "",
        street: "",
        street2: "",
        city: "",
        zipcode: "",
        state: "",
        country: "",
        metaJson: keyExists("metadata.raw", device) ? JSON.stringify(device.metadata.raw) : "",
    };

    function getFromDeviceMeta(key, device, defaultValue) {
        return keyExists(`metadata.raw.${key}`, device) ? keyExists(`metadata.raw.${key}`, device, false,true) : defaultValue;
    }

    meta.lon = keyExists("metadata.raw.coordinates_lon", device) ? device.metadata.raw.coordinates_lon : meta.lon;
    meta.lat = keyExists("metadata.raw.coordinates_lat", device) ? device.metadata.raw.coordinates_lat : meta.lat;
    
    meta.street = getFromDeviceMeta("address.street", device, meta.street);
    meta.street2 = getFromDeviceMeta("address.street2", device, meta.street2);
    meta.city = getFromDeviceMeta("address.city", device, meta.city);
    meta.zipcode = getFromDeviceMeta("address.zipcode", device, meta.zipcode);
    meta.state = getFromDeviceMeta("address.state", device, meta.state);
    meta.country = getFromDeviceMeta("address.country", device, meta.country);
    
    
    

    return meta;
}
